import { inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { OAuthService } from 'angular-oauth2-oidc';
import { catchError, from, map, Observable, of, shareReplay, switchMap, tap } from 'rxjs';
import { UserResult } from '@nx-customer-apps/shared/interfaces';
import { ApplicationRoles, IdentityTypes, UserProperties } from '@nx-customer-apps/shared/enums';
import { AppSwitcherFacade } from '@vi-succ/vi-header-domain';
import { AppSwitcherContent } from '@vi-succ/design-system-web';
import { WindowService } from '@nx-customer-apps/shared/services';
import { Routes } from '../../shared/enums';
import { EnvironmentService } from './environment.service';

@Injectable({
    providedIn: 'root'
})
export class AuthService {
    private oauth = inject(OAuthService);
    private http = inject(HttpClient);
    private env = inject(EnvironmentService);
    private windowService = inject(WindowService);
    private router = inject(Router);
    private appSwitcherService = inject(AppSwitcherFacade);

    private userResultRequest$: Observable<UserResult> | undefined;

    private _authenticated = signal<boolean>(false);
    private _authenticating = signal<boolean>(true);
    private _authorizationDone = signal<boolean>(false);
    private _appSwitcherContent = signal<AppSwitcherContent | null>(null);
    private _user = signal<UserResult | null>(null);
    private _userLoaded = signal(false);

    authenticated = this._authenticated.asReadonly();
    authenticating = this._authenticating.asReadonly();
    authorizationDone = this._authorizationDone.asReadonly();
    appSwitcherContent = this._appSwitcherContent.asReadonly();
    user = this._user.asReadonly();
    userLoaded = this._userLoaded.asReadonly();

    constructor() {
        this.initOAuth();
    }

    runInitialLoginSequence(): Observable<void> {
        return from(this.oauth.loadDiscoveryDocument()).pipe(
            switchMap(() => from(this.oauth.tryLoginCodeFlow())),
            tap(() => {
                this._authenticated.set(this.oauth.hasValidAccessToken());
            }),
            tap(() => {
                this._authenticating.set(false);
                if (this.oauth.state) {
                    let stateUrl = this.oauth.state;
                    if (!stateUrl.startsWith('/')) {
                        stateUrl = decodeURIComponent(stateUrl);
                    }
                    this.windowService.nativeWindow.location.href = stateUrl;
                }
            }),
            catchError(err => {
                this._authenticating.set(false);
                this.router.navigate([Routes.PagesMaintenance]);
                return of();
            })
        );
    }

    redirectToIAMLogin(state?: string) {
        this.oauth.initCodeFlow(state);
    }

    logout() {
        this._authenticated.set(false);
        this._user.set(null);
        this.oauth.logOut();
        this.router.navigate([Routes.Home]);
    }

    loadUserResult(): Observable<UserResult> {
        const userLoaded = this._userLoaded();
        const user = this._user();
        if (userLoaded && user) {
            return of(user);
        }

        if (!this.userResultRequest$) {
            this._authorizationDone.set(false);
            this.userResultRequest$ = this.getUserResult().pipe(
                switchMap(user =>
                    this.getUserApps(user.id).pipe(
                        map(() => user),
                        catchError(() => of(user))
                    )
                ),
                tap(() => {
                    this._authorizationDone.set(true);
                }),
                shareReplay({ bufferSize: 1, refCount: true }),
                catchError(error => {
                    this.userResultRequest$ = undefined;
                    throw error;
                })
            );
        }
        return this.userResultRequest$;
    }

    isAuthorizedForIdentityType(requiredIdentityTypes: IdentityTypes[] | IdentityTypes): boolean {
        const allowedIdentityTypes = Array.isArray(requiredIdentityTypes) ? requiredIdentityTypes : [requiredIdentityTypes];
        const identityType = this._user()?.properties.find(prop => prop.name === UserProperties.IdentityType);
        if (!identityType) {
            return false;
        }
        return allowedIdentityTypes.includes(identityType.value as IdentityTypes);
    }

    isAuthorizedForApplicationRole(requiredApplicationRoles: ApplicationRoles[] | ApplicationRoles): boolean {
        const allowedApplicationRoles = Array.isArray(requiredApplicationRoles) ? requiredApplicationRoles : [requiredApplicationRoles];
        const userApplicationRoles = this._user()?.applicationRoles;
        if (!userApplicationRoles) {
            return false;
        }
        return allowedApplicationRoles.some(role => userApplicationRoles.includes(role));
    }

    markAuthorizationDone() {
        this._authorizationDone.set(true);
    }

    private initOAuth() {
        this.oauth.configure(this.env.authConfig);
        this.oauth.setupAutomaticSilentRefresh();
        this.handleOAuthEvents();
    }

    private handleOAuthEvents() {
        this.oauth.events.subscribe(() => {
            this._authenticated.set(this.oauth.hasValidAccessToken());
        });
    }

    private getUserResult(): Observable<UserResult> {
        return this.http.get<UserResult>(this.env.userResultUrl).pipe(
            tap(user => {
                this._user.set(user);
                this._userLoaded.set(true);
            })
        );
    }

    private getUserApps(userId: string): Observable<AppSwitcherContent> {
        return this.appSwitcherService.getApps(userId).pipe(
            tap(content => {
                this._appSwitcherContent.set(content);
            })
        );
    }
}
